import { useContext, createContext, FC, PropsWithChildren, useCallback } from 'react';
import { noop } from 'utils/noop';

import { DynamicContent } from '../domain';
import { serializeDynamicContent } from '../utils/serializeDynamicContent';
import { ContentType, InsertFn } from '../types';

export type DynamicContentContextState = {
  insert: (handlebars: DynamicContent) => void;
  onClose: () => void;
  contentType: ContentType;
};

export const dynamicContentDefaultState = {
  insert: noop,
  onClose: noop,
  contentType: ContentType.html,
};

export const DynamicContentContext = createContext<DynamicContentContextState>(
  dynamicContentDefaultState,
);

export const useDynamicContentContext = () => useContext(DynamicContentContext);

type DynamicContentContextProviderProps = {
  onInsert: InsertFn;
  onClose: () => void;
  contentType: ContentType;
};

export const DynamicContentContextProvider: FC<
  PropsWithChildren<DynamicContentContextProviderProps>
> = ({ children, onInsert, onClose, contentType }) => {
  const insert = useCallback(
    (content: DynamicContent) => {
      const rawHandlebar = serializeDynamicContent(content);
      onInsert(rawHandlebar);
    },
    [onInsert],
  );

  const value = {
    insert,
    onClose,
    contentType,
  };

  return <DynamicContentContext.Provider value={value}>{children}</DynamicContentContext.Provider>;
};
